// What the marketing team types for one month of the owner report (a note, what they need from the owner, the numbers
// Hayven can't see), and the staff-only "ready to send" check. `teamInput` is part of the payload in
// `owner-report-schema.ts`; the readiness never is: only the staff preview carries it (`ownerReportStaffPageSchema`).
import { z } from 'zod';
import { ownerReportTeamMemberSchema, type OwnerReportTeamMember } from './owner-report-team-work-schema';

/** Longest note to the owner. */
export const OWNER_REPORT_TEAM_NOTE_MAX_LENGTH = 600;
/** Longest "What we need from you". */
export const OWNER_REPORT_OWNER_ASK_MAX_LENGTH = 300;
/** Largest typed number (views can run to millions). */
export const OWNER_REPORT_WORK_NUMBER_MAX = 9_999_999;

/** The numbers the team types each month. All optional: a blank one is hidden on the page, never shown as 0. */
export const OWNER_REPORT_WORK_NUMBER_KEYS = [
  'postsPublished',
  'reelsEdited',
  'filmingDays',
  'adCampaigns',
  'newAdsTested',
  'weakAdsPaused',
  'views',
  'followersGained',
] as const;
export type OwnerReportWorkNumberKey = (typeof OWNER_REPORT_WORK_NUMBER_KEYS)[number];

/** The page's groups, in its order: Content, Ads, Social reach. */
export const OWNER_REPORT_WORK_NUMBER_GROUPS = [
  { group: 'CONTENT', keys: ['postsPublished', 'reelsEdited', 'filmingDays'] },
  { group: 'ADS', keys: ['adCampaigns', 'newAdsTested', 'weakAdsPaused'] },
  { group: 'SOCIAL_REACH', keys: ['views', 'followersGained'] },
] as const satisfies readonly { group: string; keys: readonly OwnerReportWorkNumberKey[] }[];

export const ownerReportWorkNumberSchema = z.number().int().min(0).max(OWNER_REPORT_WORK_NUMBER_MAX);

const numberOrNull = ownerReportWorkNumberSchema.nullable();
/** Every key present: null = not given. */
export const ownerReportWorkNumbersSchema = z.object({
  postsPublished: numberOrNull,
  reelsEdited: numberOrNull,
  filmingDays: numberOrNull,
  adCampaigns: numberOrNull,
  newAdsTested: numberOrNull,
  weakAdsPaused: numberOrNull,
  views: numberOrNull,
  followersGained: numberOrNull,
}) satisfies z.ZodType<Record<OwnerReportWorkNumberKey, number | null>>;
export type OwnerReportWorkNumbers = z.infer<typeof ownerReportWorkNumbersSchema>;

/** All blank. */
export const EMPTY_OWNER_REPORT_WORK_NUMBERS: OwnerReportWorkNumbers = Object.freeze(
  Object.fromEntries(OWNER_REPORT_WORK_NUMBER_KEYS.map((key) => [key, null])) as OwnerReportWorkNumbers
);

/** What the team wrote for the month; null when they wrote nothing (nor a plan: that one is `nextMonthPlan`). */
export const ownerReportTeamInputSchema = z.object({
  note: z.string().nullable(),
  /** The first of `team`, who signs the note; null without a note or a team. */
  signedBy: ownerReportTeamMemberSchema.nullable(),
  ownerAsk: z.string().nullable(),
  numbers: ownerReportWorkNumbersSchema,
});
export type OwnerReportTeamInput = z.infer<typeof ownerReportTeamInputSchema>;

/**
 * What a staff save answers: every input of the month as saved (blank = null). The defaults read an older API's answer,
 * which holds the plan alone, in the minutes between deploys.
 */
export const ownerReportSavedMonthSchema = z.object({
  month: z.string(),
  nextMonthPlan: z.string().nullable(),
  teamNote: z.string().nullable().default(null),
  ownerAsk: z.string().nullable().default(null),
  workNumbers: ownerReportWorkNumbersSchema.default(EMPTY_OWNER_REPORT_WORK_NUMBERS),
});
export type OwnerReportSavedMonth = z.infer<typeof ownerReportSavedMonthSchema>;
export type OwnerReportSavedTeamInput = Omit<OwnerReportSavedMonth, 'month'>;

/** The payload's `teamInput` from the saved inputs; the API and the staff page's cache build it the same way. */
export function ownerReportTeamInputOf(
  saved: Omit<OwnerReportSavedTeamInput, 'nextMonthPlan'>,
  team: readonly OwnerReportTeamMember[] | null
): OwnerReportTeamInput | null {
  const anyNumber = OWNER_REPORT_WORK_NUMBER_KEYS.some((key) => saved.workNumbers[key] !== null);
  if (saved.teamNote === null && saved.ownerAsk === null && !anyNumber) return null;
  return {
    note: saved.teamNote,
    signedBy: saved.teamNote === null ? null : (team?.[0] ?? null),
    ownerAsk: saved.ownerAsk,
    numbers: { ...saved.workNumbers },
  };
}

/** One line of the ready check, in the order the team reads it. */
export const OWNER_REPORT_READINESS_KEYS = [
  'GUESTS',
  'NOTE',
  'PLAN',
  'TEAM',
  'META_ADS',
  'GOOGLE_ADS',
  'GOOGLE_ADS_CURRENCY',
  'GOOGLE_BUSINESS',
  'GOOGLE_PROFILE_MONTH',
  'STAND',
  'AVERAGE_TICKET',
] as const;
export type OwnerReportReadinessKey = (typeof OWNER_REPORT_READINESS_KEYS)[number];

/**
 * A check warns, it never blocks sending. The staff page links each key to where it is fixed. `key` is open on purpose: a
 * tab left open across a deploy shows a line it doesn't know, rather than failing the whole report.
 */
export const ownerReportReadinessCheckSchema = z.object({
  key: z.string(),
  ok: z.boolean(),
  /** One plain sentence. */
  text: z.string(),
});
export type OwnerReportReadinessCheck = z.infer<typeof ownerReportReadinessCheckSchema>;
